import { useRef } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useAISummaryData from "../hooks/useAISummaryData";
import AISummarySkeleton from "./AISummarySkeleton";
import "github-markdown-css/github-markdown-light.css";
import "./AISummaryContent.css";

/**
 * AI Summary content: lists this model's summary markdown files from ADLS and
 * renders them as switchable sections. Visibility is owned by the parent —
 * mount it when the AI Summary tab is active, unmount otherwise.
 */
const AISummaryContent = ({ guuid, folder }) => {
  const {
    tabs,
    activeTab,
    setActiveTab,
    content,
    loading,
    listLoading,
    listError,
    fileList,
    hasPath,
  } = useAISummaryData(guuid, folder);
  const segmentRefs = useRef([]);

  const moveSegmentFocus = (idx, delta) => {
    const next = (idx + delta + tabs.length) % tabs.length;
    setActiveTab(next);
    segmentRefs.current[next]?.focus();
  };

  if (!hasPath) {
    return (
      <div className="ai-summary-empty">
        <p>No summary data available for this model.</p>
      </div>
    );
  }

  return (
    <div className="ai-summary-panel">
      <div className="ai-summary-banner" role="note">
        <FaExclamationTriangle
          className="ai-summary-banner-icon"
          aria-hidden="true"
        />
        <span>
          <b>AI-generated</b> from the model report — no external data or
          interpretations added. Verify against the full report before relying
          on it.
        </span>
      </div>
      {tabs.length > 0 && (
        <div
          className="summary-segmented"
          role="tablist"
          aria-label="AI summary sections"
        >
          {tabs.map((tab, idx) => (
            <button
              key={tab.filename}
              ref={(el) => {
                segmentRefs.current[idx] = el;
              }}
              id={`summary-segment-${idx}`}
              type="button"
              role="tab"
              aria-selected={idx === activeTab}
              aria-controls="summary-segment-panel"
              tabIndex={idx === activeTab ? 0 : -1}
              className={`summary-segment${idx === activeTab ? " active" : ""}`}
              onClick={() => setActiveTab(idx)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight") {
                  e.preventDefault();
                  moveSegmentFocus(idx, 1);
                } else if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  moveSegmentFocus(idx, -1);
                }
              }}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}
      <div
        id="summary-segment-panel"
        role="tabpanel"
        aria-labelledby={
          tabs.length > 0 ? `summary-segment-${activeTab}` : undefined
        }
        className="ai-summary-markdown markdown-body"
      >
        {listLoading && !fileList?.length ? (
          <AISummarySkeleton />
        ) : listError ? (
          <div className="ai-summary-error">
            Could not load summary list: {listError}
          </div>
        ) : !tabs.length ? (
          <div className="ai-summary-empty">
            <p>No AI summaries available for this model.</p>
          </div>
        ) : loading ? (
          <AISummarySkeleton />
        ) : (
          <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>
            {content}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
};

export default AISummaryContent;
